import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './ChainVisualizer.css';

const ChainVisualizer = () => {
  const canvasRef = useRef(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const chains = [
    { id: 'ethereum', name: 'Ethereum', color: '#627EEA', angle: 0 },
    { id: 'bnb', name: 'BNB Chain', color: '#F0B90B', angle: 45 },
    { id: 'solana', name: 'Solana', color: '#14F195', angle: 90 },
    { id: 'avalanche', name: 'Avalanche', color: '#E84142', angle: 135 },
    { id: 'cosmos', name: 'Cosmos', color: '#2E3148', angle: 180 },
    { id: 'polkadot', name: 'Polkadot', color: '#E6007A', angle: 225 },
    { id: 'cardano', name: 'Cardano', color: '#0033AD', angle: 270 },
    { id: 'algorand', name: 'Algorand', color: '#000000', angle: 315 },
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 200;

    let angle = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw center node
      ctx.beginPath();
      ctx.arc(centerX, centerY, 50, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 212, 255, 0.2)';
      ctx.fill();
      ctx.strokeStyle = '#00d4ff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw data flow connections
      chains.forEach((chain, index) => {
        const chainAngle = (chain.angle + angle) * (Math.PI / 180);
        const x = centerX + Math.cos(chainAngle) * radius;
        const y = centerY + Math.sin(chainAngle) * radius;

        // Draw connection line with glow
        const gradient = ctx.createLinearGradient(centerX, centerY, x, y);
        gradient.addColorStop(0, '#00d4ff');
        gradient.addColorStop(1, chain.color);

        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(x, y);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.3;
        ctx.stroke();
        ctx.globalAlpha = 1;

        // Draw data packet moving along line
        const packetProgress = ((Date.now() / 2000 + index * 0.2) % 1);
        const packetX = centerX + (x - centerX) * packetProgress;
        const packetY = centerY + (y - centerY) * packetProgress;

        ctx.beginPath();
        ctx.arc(packetX, packetY, 4, 0, Math.PI * 2);
        ctx.fillStyle = chain.color;
        ctx.shadowBlur = 15;
        ctx.shadowColor = chain.color;
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      angle += 0.1;
      requestAnimationFrame(animate);
    };

    animate();
  }, []);

  return (
    <section className="visualizer-alien" data-testid="chain-visualizer-section" ref={ref}>
      <div className="section-container">
        <motion.div
          className="section-header-alien"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="section-tag">
            <div className="tag-line"></div>
            <span className="tag-text">NETWORK TOPOLOGY</span>
            <div className="tag-line"></div>
          </div>
          <h2 className="section-title-alien">
            <span className="title-normal">MULTI-CHAIN</span>
            <span className="title-highlight">CONNECTIVITY</span>
          </h2>
          <p className="section-subtitle-alien">
            Real-time unified settlement across major Layer-1 blockchain ecosystems
          </p>
        </motion.div>

        <div className="visualizer-grid">
          {/* Canvas Visualization */}
          <motion.div
            className="canvas-wrapper"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8 }}
          >
            <canvas ref={canvasRef} width="500" height="500" className="network-canvas" />
            <div className="center-label">
              <div className="center-pulse"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_592594db-80a8-424b-aef8-5e7847c76b85/artifacts/sl8inwlx_NOMOEX%20Logo%20%281%29.png"
                alt="Nomoex Core"
                className="center-n-icon"
              />
              <div className="center-subtext">CORE</div>
            </div>
          </motion.div>

          {/* Chain Details */}
          <motion.div
            className="chains-details"
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 className="details-title">Connected L1 Networks</h3>
            <div className="chains-list">
              {chains.map((chain, index) => (
                <motion.div
                  key={chain.id}
                  className="chain-detail-item"
                  initial={{ opacity: 0, x: 20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.4 + index * 0.05 }}
                  style={{ '--chain-color': chain.color }}
                >
                  <div className="chain-status" style={{ background: chain.color }}></div>
                  <div className="chain-detail-name">{chain.name}</div>
                  <div className="chain-indicator">ACTIVE</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          className="visualizer-info"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="info-card">
            <div className="info-label">Settlement Method</div>
            <div className="info-value">Multi-L1 Proof Aggregation</div>
          </div>
          <div className="info-divider"></div>
          <div className="info-card">
            <div className="info-label">Transaction Type</div>
            <div className="info-value">Atomic Cross-Chain</div>
          </div>
          <div className="info-divider"></div>
          <div className="info-card">
            <div className="info-label">Security Model</div>
            <div className="info-value">ZK + Optimistic Hybrid</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ChainVisualizer;